import type { AxiosInstance } from "axios";
import {
  U3134WorkInProgressLineBillableUpdateSchema,
} from "./U3134WorkInProgressApiSchema";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { u2000SkipBusyIndicatorConfig } from "../u2000Core/axios/U2000AxiosRequestConfig";
import type { U2000SearchPayload, U2000SearchParameter } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000_AGING_BUCKETS, type U2000AgingBucket } from "../u2000Core/constants/U2000AgingBuckets";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import { formatCurrency } from "../u2000Core/tools/U2000CurrencyUtils";
import { U2000ApiValueToDisplayYmd, u2000TodayOrgYmd } from "../u2000Core/tools/U2000DateTimeHelpers";
import type { U3130FindProjectItem, U3130FindSubprojectItem } from "../u3130TimesheetApproval/U3130TimesheetModel";

const LOGICALUNIT_URL = "U3134";

export const U3134_TRANSACTION_KIND = {
  Honorarium: "honorarium",
  Expense: "expense",
  Equipment: "equipment",
} as const;

export type U3134TransactionKind = (typeof U3134_TRANSACTION_KIND)[keyof typeof U3134_TRANSACTION_KIND];

/** Aligné sur le backend `prWorkInProgressHonorariumEntryKind`. */
export const U3134_HONORARIUM_ENTRY_KIND = {
  SubProjectEmployeeTask: "subProjectEmployeeTask",
  SubProjectEmployee: "subProjectEmployee",
  SubProjectTask: "subProjectTask",
  Employee: "employee",
  ProjectEmployeeTask: "projectEmployeeTask",
  ProjectEmployee: "projectEmployee",
  ProjectTask: "projectTask",
  SubProject: "subProject",
  CustomerEmployeeTask: "customerEmployeeTask",
  CustomerEmployee: "customerEmployee",
  CustomerTask: "customerTask",
  Project: "project",
  Customer: "customer",
  /** Valeur historique ; conserver pour l’affichage de lignes existantes. */
  Partner: "partner",
} as const;

export type U3134HonorariumEntryKind = (typeof U3134_HONORARIUM_ENTRY_KIND)[keyof typeof U3134_HONORARIUM_ENTRY_KIND];

/** Ordre d’affichage dans le combo « type d’entrée honoraires ». */
export const U3134_HONORARIUM_ENTRY_KIND_UI_ORDER: readonly U3134HonorariumEntryKind[] = [
  U3134_HONORARIUM_ENTRY_KIND.SubProjectEmployeeTask,
  U3134_HONORARIUM_ENTRY_KIND.SubProjectEmployee,
  U3134_HONORARIUM_ENTRY_KIND.SubProjectTask,
  U3134_HONORARIUM_ENTRY_KIND.Employee,
  U3134_HONORARIUM_ENTRY_KIND.ProjectEmployeeTask,
  U3134_HONORARIUM_ENTRY_KIND.ProjectEmployee,
  U3134_HONORARIUM_ENTRY_KIND.ProjectTask,
  U3134_HONORARIUM_ENTRY_KIND.SubProject,
  U3134_HONORARIUM_ENTRY_KIND.CustomerEmployeeTask,
  U3134_HONORARIUM_ENTRY_KIND.CustomerEmployee,
  U3134_HONORARIUM_ENTRY_KIND.CustomerTask,
  U3134_HONORARIUM_ENTRY_KIND.Project,
  U3134_HONORARIUM_ENTRY_KIND.Customer,
  U3134_HONORARIUM_ENTRY_KIND.Partner,
];

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GENERATE_WIP: string;
  GET_JOB: (jobId: string) => string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
  FINDPROJECT: string;
  FINDINVOICE: string;
  FINDEMPLOYEE: string;
  TEC_AGE_REPORT: string;
  TEC_AGE_SUMMARY: string;
  TEC_AGE_CUSTOMER_DETAIL: string;
  TEC_AGE_EXPORT_EXCEL: string;
  TEC_AGE_EXPORT_PDF: string;
  EXPORT_EXCEL: string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GENERATE_WIP: `${LOGICALUNIT_URL}/generateWip`,
  GET_JOB: (jobId: string) => `${LOGICALUNIT_URL}/jobs/${jobId}`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
  FINDPROJECT: `${LOGICALUNIT_URL}/findProject`,
  FINDINVOICE: `${LOGICALUNIT_URL}/findInvoice`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
  TEC_AGE_REPORT: `${LOGICALUNIT_URL}/reports/tecAge`,
  TEC_AGE_SUMMARY: `${LOGICALUNIT_URL}/reports/tecAge/summary`,
  TEC_AGE_CUSTOMER_DETAIL: `${LOGICALUNIT_URL}/reports/tecAge/customerDetail`,
  TEC_AGE_EXPORT_EXCEL: `${LOGICALUNIT_URL}/reports/tecAge/exportExcel`,
  TEC_AGE_EXPORT_PDF: `${LOGICALUNIT_URL}/reports/tecAge/exportPdf`,
  EXPORT_EXCEL: `${LOGICALUNIT_URL}/exportExcel`,
};

export interface U3134InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode?: string;
  /** Fuseau d'affichage organisation (IANA). Défaut frontend : America/Montreal. */
  organizationTimezone?: string;
}

export interface U3134ProjectRef {
  id: string;
  projetNumber?: string;
  projetName?: string;
  customer?: {
    id?: string;
    customerCode?: string;
    customerName?: string;
  } | null;
}

export interface U3134SubprojectRef {
  id: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

export interface U3134TaskRef {
  id: string;
  taskCode?: string;
  taskDescriptionFr?: string | null;
  taskDescriptionEn?: string | null;
}

export interface U3134EmployeeRef {
  id: string;
  codeEmployee?: string;
  numEmployee?: string;
  firstName?: string;
  lastName?: string;
}

export interface U3134TimesheetLineRef {
  id?: string;
}

/** Type de dépense (présent sur GET quand `transactionKind === expense` selon l’API). */
export interface U3134ExpenseTypeRef {
  code?: string | null;
  description?: string | null;
}

/** Facture U3125 liée à la ligne TEC (GET / search). */
export interface U3134InvoiceRef {
  id: string;
  invoiceNumber: string | null;
}

/** Ligne retournée par POST /U3134/search ou GET /U3125/wipTransactions (forme normalisée). */
export interface U3134WorkInProgressSearchRow {
  id: string;
  transactionKind?: U3134TransactionKind;
  honorariumEntryKind?: U3134HonorariumEntryKind | null;
  transactionDate?: string | Date;
  billable?: boolean;
  /** `false` = forfaitaire (afficher coutant seulement) ; `true` = horaire (coutant + vendant). */
  hourly?: boolean | null;
  sellingRate?: number | null;
  costRate?: number | null;
  quantity?: number;
  sellingMarkupFactor?: number;
  costMarkupFactor?: number;
  /** Montant vendant. */
  amount?: number;
  /** Montant coutant. */
  costAmount?: number;
  /** Montant reportable en liste (forfait → costAmount, horaire → amount) — BL-0438 / BL-0073. */
  reportableAmount?: number;
  remark?: string | null;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
  project?: U3134ProjectRef | null;
  employee?: U3134EmployeeRef | null;
  expenseType?: U3134ExpenseTypeRef | null;
  equipmentType?: U3134ExpenseTypeRef | null;
  /** Facture liée ; `null` = TEC non facturée. */
  invoice?: U3134InvoiceRef | null;
  /** Tâche FT persistée sur la ligne (search BL-0655) ; `null` si absente. */
  task?: U3134TaskRef | null;
  /** @deprecated Alias legacy — préférer `sellingRate`. */
  rate?: number;
  /** @deprecated Alias legacy — préférer `sellingMarkupFactor`. */
  markupFactor?: number;
}

/** Détail GET /U3134/:id (sans organization). */
export interface U3134WorkInProgressLineDetail {
  id: string;
  transactionKind: U3134TransactionKind;
  honorariumEntryKind?: U3134HonorariumEntryKind | null;
  transactionDate: string;
  billable: boolean;
  /** Profil de facturation résolu à la génération TEC (`billingProfile.hourly`). */
  hourly?: boolean | null;
  /** Taux vendant horaire (GET) ; `null` en forfaitaire. */
  sellingRate?: number | null;
  /** Taux coût unitaire (GET) ; `null` si non applicable. */
  costRate?: number | null;
  quantity: number;
  sellingMarkupFactor: number;
  costMarkupFactor: number;
  amount: number;
  costAmount: number;
  remark?: string | null;
  version: number;
  project: U3134ProjectRef;
  subproject?: U3134SubprojectRef | null;
  task?: U3134TaskRef | null;
  employee?: U3134EmployeeRef | null;
  timesheetHonorarium?: U3134TimesheetLineRef | null;
  timesheetExpense?: U3134TimesheetLineRef | null;
  timesheetEquipment?: U3134TimesheetLineRef | null;
  expenseType?: U3134ExpenseTypeRef | null;
  /** Aligné sur `expenseType` (code + libellé) si l’API expose le type d’équipement sur la ligne FDH. */
  equipmentType?: U3134ExpenseTypeRef | null;
  /** Facture liée ; `null` = TEC non facturée. */
  invoice?: U3134InvoiceRef | null;
}

export interface U3134WorkInProgressFormRow {
  id: string;
  version: number;
  project: U3130FindProjectItem | null;
  subproject: U3130FindSubprojectItem | null;
  clientDisplay: string;
  employeeDisplay: string;
  taskId: string;
  taskCode: string;
  taskDescription: string;
  transactionKind: U3134TransactionKind;
  honorariumEntryKind: U3134HonorariumEntryKind | null;
  timesheetHonorariumId: string;
  timesheetExpenseId: string;
  timesheetEquipmentId: string;
  /** Affichage (GET) du type de dépense lié à la ligne FDH. */
  expenseTypeCode: string;
  expenseTypeDescription: string;
  /** Affichage (GET) du type d’équipement lié à la ligne FDH. */
  equipmentTypeCode: string;
  equipmentTypeDescription: string;
  transactionDate: string;
  billable: boolean;
  /** Lecture seule (GET) : `true` = horaire, `false` = forfaitaire. */
  hourly: boolean | null;
  /** Lecture seule (GET) : taux vendant si horaire. */
  sellingRate: number | null;
  /** Taux coût (honoraires / équipement) ; `null` si l’API n’expose pas de taux. */
  costRate: number | null;
  quantity: number;
  sellingMarkupFactor: number;
  costMarkupFactor: number;
  /** Dépense : montant unitaire de base ; `null` si `costRate` API absent. */
  unitBaseAmount: number | null;
  amount: number;
  costAmount: number;
  /** Dépense / équipement : taux vendant ; `null` si `sellingRate` API absent. */
  sellingUnitRate: number | null;
  remark: string;
  /** Facture liée ; `null` = TEC non facturée. */
  invoice: U3134InvoiceRef | null;
}

/** Ligne TEC rattachée à une facture (standard ou radiation WWO). */
export function isU3134InvoicedLine(row: { invoice?: U3134InvoiceRef | null } | null | undefined): boolean {
  return Boolean(row?.invoice?.id?.trim());
}

/** Projet forfaitaire (`hourly === false` sur GET) : vendant à 0 pour honoraires, dépenses et équipement. */
export function isU3134ProjectForfaitSellingLine(
  row: Pick<U3134WorkInProgressFormRow, "hourly">,
): boolean {
  return row.hourly === false;
}

/** Honoraires forfaitaire TEC : `hourly === false` (fallback legacy : `amount === 0`). */
export function isU3134HonorariumFixedPriceLine(
  row: Pick<U3134WorkInProgressFormRow, "transactionKind" | "amount" | "hourly">,
): boolean {
  if (row.transactionKind !== U3134_TRANSACTION_KIND.Honorarium) return false;
  if (row.hourly === true) return false;
  if (row.hourly === false) return true;
  return row.amount === 0;
}

/** Honoraires en facturation horaire (`hourly === true`). */
export function isU3134HonorariumHourlyLine(
  row: Pick<U3134WorkInProgressFormRow, "transactionKind" | "hourly">,
): boolean {
  return row.transactionKind === U3134_TRANSACTION_KIND.Honorarium && row.hourly === true;
}

/** Taux API > 0 pour affichage formulaire ; sinon `null` (pas de dérivation depuis les montants). */
export function u3134ApiRateToFormValue(rate: number | null | undefined): number | null {
  if (rate == null || !Number.isFinite(Number(rate)) || Number(rate) <= 0) {
    return null;
  }
  return Number(rate);
}

/** Taux coût employé affiché dans le panel coutant (API `costRate` ou dérivé des montants). */
export function u3134HonorariumCostRate(
  row: Pick<U3134WorkInProgressFormRow, "hourly" | "costRate" | "quantity" | "costMarkupFactor" | "costAmount">,
): number {
  if (row.costRate != null && Number.isFinite(row.costRate) && row.costRate > 0) {
    return row.costRate;
  }
  if (row.hourly === false) {
    return row.costRate ?? 0;
  }
  const qty = Number(row.quantity);
  const markup = Number(row.costMarkupFactor);
  const cost = Number(row.costAmount);
  if (!Number.isFinite(qty) || qty <= 0 || !Number.isFinite(markup) || markup <= 0 || !Number.isFinite(cost)) {
    return 0;
  }
  return Math.round((cost / (qty * markup)) * 100) / 100;
}

/** Taux vendant affiché dans le panel vendant. */
export function u3134HonorariumSellingRate(row: Pick<U3134WorkInProgressFormRow, "sellingRate">): number {
  if (row.sellingRate != null && Number.isFinite(row.sellingRate)) {
    return row.sellingRate;
  }
  return 0;
}

export interface U3134WorkInProgressLineCreateBody {
  projectId: string;
  subprojectId?: string | null;
  taskId?: string | null;
  transactionKind: U3134TransactionKind;
  honorariumEntryKind?: U3134HonorariumEntryKind | null;
  timesheetHonorariumId?: string | null;
  timesheetExpenseId?: string | null;
  timesheetEquipmentId?: string | null;
  transactionDate: string;
  billable: boolean;
  sellingRate?: number | null;
  costRate?: number | null;
  quantity: number;
  sellingMarkupFactor: number;
  costMarkupFactor: number;
  unitBaseAmount?: number | null;
  amount: number;
  costAmount: number;
  remark?: string | null;
}

/** Corps update minimal — seul Facturable est modifiable depuis l’UI U3134. */
export interface U3134WorkInProgressLineBillableUpdateBody {
  id: string;
  version: number;
  billable: boolean;
}

/** @deprecated Alias — même contrat que billable-only. */
export type U3134WorkInProgressLineInvoicedUpdateBody = U3134WorkInProgressLineBillableUpdateBody;

export type U3134WorkInProgressLineUpdatePayload = U3134WorkInProgressLineBillableUpdateBody;

export interface U3134WorkInProgressLineDeleteBody {
  id: string;
  version: number;
}

export interface U3134GenerateWipJobData {
  createdLineIds?: string[];
  phaseB?: {
    createdLineIds?: string[];
    linesCreated?: number;
    processed?: number;
    failures?: unknown[];
  };
}

export interface U3134GenerateWipJob {
  id: string;
  status?: string;
  progressPercent?: number;
  progressData?: U3134GenerateWipProgressData | null;
  /** Résultat métier du job (GET /jobs/:id après completion). */
  data?: U3134GenerateWipJobData | null;
  result?: {
    data?: U3134GenerateWipJobData | null;
  } | null;
}

/**
 * Extrait les IDs de lignes TEC créées par un run generateWip.
 * Préfère `data.createdLineIds`, sinon `data.phaseB.createdLineIds` (directives BL-0425).
 */
export function u3134ExtractCreatedLineIds(job: unknown): string[] {
  if (!isRecord(job)) return [];
  const dataCandidate =
    (isRecord(job.data) ? job.data : null) ??
    (isRecord(job.result) && isRecord(job.result.data) ? job.result.data : null);
  if (!dataCandidate) return [];
  const rootIds = dataCandidate.createdLineIds;
  if (Array.isArray(rootIds) && rootIds.every((id) => typeof id === 'string')) {
    return rootIds.filter((id) => id.length > 0);
  }
  const phaseB = dataCandidate.phaseB;
  if (isRecord(phaseB) && Array.isArray(phaseB.createdLineIds)) {
    return phaseB.createdLineIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }
  return [];
}

/** Réponse 201 POST /U3134/generateWip : enveloppe + alias jobId. */
export interface U3134GenerateWipPostResponse {
  type?: string;
  result: U3134GenerateWipJob;
  /** Alias de `result.id` (pratique si `result` est incomplet côté filtre sérialisation). */
  jobId?: string;
}

/** Réponse 200 GET /U3134/jobs/:jobId : même enveloppe que les autres résultats. */
export interface U3134JobStatusGetResponse {
  type?: string;
  result: U3134GenerateWipJob;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/**
 * Extrait le job depuis la réponse POST generateWip (enveloppe { type, result, jobId } ou job plat legacy).
 */
export function parseGenerateWipPostResponse(data: unknown): U3134GenerateWipJob {
  if (isRecord(data) && isRecord(data.result)) {
    const r = data.result as unknown as U3134GenerateWipJob & Record<string, unknown>;
    const aliasId = typeof data.jobId === "string" ? data.jobId : undefined;
    const id = typeof r.id === "string" && r.id !== "" ? r.id : aliasId;
    if (id) {
      return { ...(r as unknown as U3134GenerateWipJob), id };
    }
  }
  if (isRecord(data) && typeof data.id === "string") {
    return data as unknown as U3134GenerateWipJob;
  }
  throw new Error("Invalid generateWip response shape");
}

/**
 * Extrait le job depuis GET /U3134/jobs/:jobId (enveloppe { type, result } ou job plat).
 */
export function parseJobStatusGetResponse(data: unknown): U3134GenerateWipJob {
  if (isRecord(data) && isRecord(data.result) && typeof data.result.id === "string") {
    return data.result as unknown as U3134GenerateWipJob;
  }
  if (isRecord(data) && typeof data.id === "string") {
    return data as unknown as U3134GenerateWipJob;
  }
  throw new Error("Invalid job status response shape");
}

export interface U3134GenerateWipCurrentItem {
  timesheetId?: string;
  employeeName?: string;
  periodDate?: string;
}

export interface U3134GenerateWipLastResult {
  timesheetId?: string;
  employeeName?: string;
  status?: "success" | "failed" | string;
}

export interface U3134GenerateWipCounters {
  promoted?: number;
  processed?: number;
  succeeded?: number;
  failed?: number;
}

export interface U3134GenerateWipProgressData {
  phase?: string;
  current?: number;
  total?: number;
  currentItem?: U3134GenerateWipCurrentItem;
  lastResult?: U3134GenerateWipLastResult;
  counters?: U3134GenerateWipCounters;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/**
 * Normalise une date en **`yyyy-mm-dd`** (pour `<input type="date" />` et corps API).
 * - Chaîne pure `yyyy-mm-dd` : renvoyée telle quelle.
 * - Horodatage ISO ou `Date` : jour civil dans le fuseau d'affichage org.
 */
export function toDateInputValue(isoOrDate: string | Date | undefined | null): string {
  if (isoOrDate == null || isoOrDate === "") return "";
  if (typeof isoOrDate === "string") {
    const trimmed = isoOrDate.trim();
    const ymd = U2000ApiValueToDisplayYmd(trimmed);
    return ymd ?? "";
  }
  const ymd = U2000ApiValueToDisplayYmd(isoOrDate);
  return ymd ?? "";
}

export function createEmptyU3134FormRow(): U3134WorkInProgressFormRow {
  return {
    id: "",
    version: 0,
    project: null,
    subproject: null,
    clientDisplay: "",
    employeeDisplay: "",
    taskId: "",
    taskCode: "",
    taskDescription: "",
    transactionKind: U3134_TRANSACTION_KIND.Honorarium,
    honorariumEntryKind: U3134_HONORARIUM_ENTRY_KIND.Employee,
    timesheetHonorariumId: "",
    timesheetExpenseId: "",
    timesheetEquipmentId: "",
    expenseTypeCode: "",
    expenseTypeDescription: "",
    equipmentTypeCode: "",
    equipmentTypeDescription: "",
    transactionDate: u2000TodayOrgYmd(),
    billable: true,
    hourly: null,
    sellingRate: null,
    costRate: null,
    quantity: 0,
    sellingMarkupFactor: 1,
    costMarkupFactor: 1,
    unitBaseAmount: null,
    amount: 0,
    costAmount: 0,
    sellingUnitRate: null,
    remark: "",
    invoice: null,
  };
}

function projectToFindItem(p: U3134ProjectRef | null | undefined): U3130FindProjectItem | null {
  if (!p?.id) return null;
  return {
    id: p.id,
    projetNumber: p.projetNumber,
    projetName: p.projetName,
    customerCode: p.customer?.customerCode,
    customerName: p.customer?.customerName,
  };
}

function subprojectToFindItem(s: U3134SubprojectRef | null | undefined, projectId: string): U3130FindSubprojectItem | null {
  if (!s?.id) return null;
  return {
    id: s.id,
    projectId,
    subProjetNumber: s.subProjetNumber,
    subProjetName: s.subProjetName,
  };
}

function clientDisplayFromProject(p: U3130FindProjectItem | U3134ProjectRef | null | undefined): string {
  if (!p) return "";
  const fp = p as U3130FindProjectItem;
  if (fp.customerName != null || fp.customerCode != null) {
    return [fp.customerName, fp.customerCode != null ? `(${fp.customerCode})` : ""].filter(Boolean).join(" ");
  }
  const rp = p as U3134ProjectRef;
  if (rp.customer?.customerName != null || rp.customer?.customerCode != null) {
    return [rp.customer?.customerName, rp.customer?.customerCode != null ? `(${rp.customer.customerCode})` : ""].filter(Boolean).join(" ");
  }
  return "";
}

function employeeDisplayFromEmployee(e: U3134EmployeeRef | null | undefined): string {
  if (!e) return "";
  const name = [e.firstName, e.lastName].filter(Boolean).join(" ").trim();
  if (name && e.codeEmployee) return `${name} (${e.codeEmployee})`;
  return name || e.codeEmployee || e.numEmployee || "";
}

function u3134MapInvoiceRef(inv: U3134InvoiceRef | null | undefined): U3134InvoiceRef | null {
  if (!inv?.id?.trim()) return null;
  const invoiceNumber =
    inv.invoiceNumber === null || inv.invoiceNumber === undefined ? null : String(inv.invoiceNumber);
  return { id: inv.id.trim(), invoiceNumber };
}

/** Complète `invoice` depuis la ligne grille si absent du GET (search déjà enrichi). */
/** Montant reportable liste (BL-0073 / BL-0438) : forfait → coutant, horaire → vendant. */
export function u3134ComputeReportableAmount(
  row: Pick<U3134WorkInProgressSearchRow, 'hourly' | 'amount' | 'costAmount'>,
): number {
  if (row.hourly === false) {
    return Number(row.costAmount) || 0;
  }
  return Number(row.amount) || 0;
}

/** Libellé grille tâche FT (BL-0655) — cascade langue alignée U3150 liste. */
export function u3134FormatTaskListLabel(
  task: U3134TaskRef | null | undefined,
  language: string,
): string {
  if (!task) return '';
  const code = task.taskCode?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? task.taskDescriptionFr : task.taskDescriptionEn)?.trim() ||
    task.taskDescriptionFr?.trim() ||
    task.taskDescriptionEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

function findItemToProjectRef(p: U3130FindProjectItem | null | undefined): U3134ProjectRef | null {
  if (!p?.id?.trim()) return null;
  return {
    id: p.id.trim(),
    projetNumber: p.projetNumber,
    projetName: p.projetName,
    customer:
      p.customerCode != null || p.customerName != null
        ? { customerCode: p.customerCode, customerName: p.customerName }
        : null,
  };
}

/** Réinjecte une ligne fiche dans la grille search sans perdre employee, createdAt, reportableAmount. */
export function u3134FormRowToSearchGridRow(
  formRow: U3134WorkInProgressFormRow,
  existing?: U3134WorkInProgressSearchRow,
): U3134WorkInProgressSearchRow {
  return {
    id: formRow.id,
    version: formRow.version,
    transactionKind: formRow.transactionKind,
    honorariumEntryKind: formRow.honorariumEntryKind,
    transactionDate: formRow.transactionDate,
    billable: formRow.billable,
    hourly: formRow.hourly,
    sellingRate: formRow.sellingRate,
    costRate: formRow.costRate,
    quantity: formRow.quantity,
    sellingMarkupFactor: formRow.sellingMarkupFactor,
    costMarkupFactor: formRow.costMarkupFactor,
    amount: formRow.amount,
    costAmount: formRow.costAmount,
    remark: formRow.remark,
    invoice: formRow.invoice,
    project: existing?.project ?? findItemToProjectRef(formRow.project),
    employee: existing?.employee,
    expenseType: existing?.expenseType,
    equipmentType: existing?.equipmentType,
    createdAt: existing?.createdAt,
    deletedAt: existing?.deletedAt,
    reportableAmount: u3134ComputeReportableAmount({
      hourly: formRow.hourly,
      amount: formRow.amount,
      costAmount: formRow.costAmount,
    }),
  };
}

export function u3134MergeInvoiceFromSearchRow(
  formRow: U3134WorkInProgressFormRow,
  searchRow: U3134WorkInProgressSearchRow | undefined,
): U3134WorkInProgressFormRow {
  if (isU3134InvoicedLine(formRow) || !searchRow?.invoice?.id?.trim()) {
    return formRow;
  }
  const mapped = u3134MapInvoiceRef(searchRow.invoice);
  if (!mapped) return formRow;
  return { ...formRow, invoice: mapped };
}

export function detailToU3134FormRow(row: U3134WorkInProgressLineDetail): U3134WorkInProgressFormRow {
  const project = projectToFindItem(row.project);
  const projectId = project?.id ?? row.project?.id ?? "";
  return {
    id: row.id,
    version: row.version,
    project,
    subproject: subprojectToFindItem(row.subproject ?? null, projectId),
    clientDisplay: clientDisplayFromProject(project ?? row.project),
    employeeDisplay: employeeDisplayFromEmployee(row.employee),
    taskId: row.task?.id ?? "",
    taskCode: row.task?.taskCode ?? "",
    taskDescription: row.task?.taskDescriptionFr ?? row.task?.taskDescriptionEn ?? "",
    transactionKind: row.transactionKind,
    honorariumEntryKind: row.honorariumEntryKind ?? null,
    timesheetHonorariumId: row.timesheetHonorarium?.id ?? "",
    timesheetExpenseId: row.timesheetExpense?.id ?? "",
    timesheetEquipmentId: row.timesheetEquipment?.id ?? "",
    expenseTypeCode: typeof row.expenseType?.code === "string" ? row.expenseType.code.trim() : "",
    expenseTypeDescription: typeof row.expenseType?.description === "string" ? row.expenseType.description.trim() : "",
    equipmentTypeCode: typeof row.equipmentType?.code === "string" ? row.equipmentType.code.trim() : "",
    equipmentTypeDescription: typeof row.equipmentType?.description === "string" ? row.equipmentType.description.trim() : "",
    transactionDate: toDateInputValue(row.transactionDate),
    billable: row.billable,
    hourly: row.hourly ?? null,
    sellingRate: row.sellingRate ?? null,
    costRate:
      row.transactionKind === U3134_TRANSACTION_KIND.Honorarium ||
      row.transactionKind === U3134_TRANSACTION_KIND.Equipment
        ? u3134ApiRateToFormValue(row.costRate)
        : null,
    quantity: row.quantity,
    sellingMarkupFactor: row.sellingMarkupFactor,
    costMarkupFactor: row.costMarkupFactor,
    unitBaseAmount:
      row.transactionKind === U3134_TRANSACTION_KIND.Expense
        ? u3134ApiRateToFormValue(row.costRate)
        : null,
    amount: row.amount,
    costAmount: row.costAmount,
    sellingUnitRate:
      row.transactionKind === U3134_TRANSACTION_KIND.Honorarium
        ? null
        : u3134ApiRateToFormValue(row.sellingRate),
    remark: row.remark ?? "",
    invoice: u3134MapInvoiceRef(row.invoice),
  };
}

function emptyToNull(s: string): string | null {
  const t = s.trim();
  return t === "" ? null : t;
}

export function buildCreateBody(row: U3134WorkInProgressFormRow): U3134WorkInProgressLineCreateBody {
  const projectId = row.project?.id?.trim() ?? "";
  const body: U3134WorkInProgressLineCreateBody = {
    projectId,
    subprojectId: row.subproject?.id ? row.subproject.id : null,
    taskId: row.taskId.trim() !== "" ? row.taskId.trim() : null,
    transactionKind: row.transactionKind,
    transactionDate: toDateInputValue(row.transactionDate),
    billable: row.billable,
    sellingRate:
      row.transactionKind === U3134_TRANSACTION_KIND.Honorarium && row.hourly === true
        ? u3134ApiRateToFormValue(row.sellingRate)
        : row.transactionKind === U3134_TRANSACTION_KIND.Expense
          ? u3134ApiRateToFormValue(row.unitBaseAmount)
          : row.transactionKind === U3134_TRANSACTION_KIND.Equipment
            ? u3134ApiRateToFormValue(row.sellingUnitRate) ?? u3134ApiRateToFormValue(row.costRate)
            : null,
    costRate:
      row.transactionKind === U3134_TRANSACTION_KIND.Expense
        ? u3134ApiRateToFormValue(row.unitBaseAmount)
        : row.transactionKind === U3134_TRANSACTION_KIND.Equipment
          ? u3134ApiRateToFormValue(row.costRate)
          : row.transactionKind === U3134_TRANSACTION_KIND.Honorarium
            ? u3134ApiRateToFormValue(row.costRate)
            : null,
    quantity: row.quantity,
    sellingMarkupFactor: row.sellingMarkupFactor,
    costMarkupFactor: row.costMarkupFactor,
    unitBaseAmount:
      row.transactionKind === U3134_TRANSACTION_KIND.Expense
        ? u3134ApiRateToFormValue(row.unitBaseAmount) != null
          ? null
          : row.costAmount > 0
            ? row.costAmount
            : null
        : null,
    amount: row.amount,
    costAmount: row.costAmount,
    remark: row.remark.trim() === "" ? null : row.remark.trim().slice(0, 500),
  };
  if (row.transactionKind === U3134_TRANSACTION_KIND.Honorarium) {
    body.honorariumEntryKind = row.honorariumEntryKind;
    body.timesheetHonorariumId = emptyToNull(row.timesheetHonorariumId);
    body.timesheetExpenseId = null;
    body.timesheetEquipmentId = null;
  } else if (row.transactionKind === U3134_TRANSACTION_KIND.Expense) {
    body.honorariumEntryKind = null;
    body.timesheetHonorariumId = null;
    body.timesheetExpenseId = emptyToNull(row.timesheetExpenseId);
    body.timesheetEquipmentId = null;
  } else {
    body.honorariumEntryKind = null;
    body.timesheetHonorariumId = null;
    body.timesheetExpenseId = null;
    body.timesheetEquipmentId = emptyToNull(row.timesheetEquipmentId);
  }
  return body;
}

export function buildUpdateBody(row: U3134WorkInProgressFormRow): U3134WorkInProgressLineUpdatePayload {
  if (isU3134InvoicedLine(row)) {
    throw new Error("U3134 invoiced line cannot be updated from the UI");
  }
  return U3134WorkInProgressLineBillableUpdateSchema.parse({
    id: row.id,
    version: row.version,
    billable: row.billable,
  });
}

export function buildDeleteBody(row: U3134WorkInProgressFormRow): U3134WorkInProgressLineDeleteBody {
  return {
    id: row.id,
    version: row.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doExportExcel = (axios: AxiosInstance, filter: U2000SearchParameter, lang?: "fr" | "en") => {
  return axios.post(LogicalUnitEndpoints.EXPORT_EXCEL, { filter, lang });
};

export const doGenerateWip = (axios: AxiosInstance) => {
  return axios.post<U3134GenerateWipPostResponse | U3134GenerateWipJob>(LogicalUnitEndpoints.GENERATE_WIP, {});
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3134WorkInProgressLineCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3134WorkInProgressLineUpdatePayload) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3134WorkInProgressLineDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

/** Statut du job de génération TEC (polling sans U2200:READ). */
export const doGetGenerateWipJob = (axios: AxiosInstance, jobId: string) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get<U3134JobStatusGetResponse | U3134GenerateWipJob>(LogicalUnitEndpoints.GET_JOB(jobId), { params });
};

export type U3134AutoCompletCriterias = { fieldName: string; value: unknown }[];

export interface U3134FindProjectItem {
  id: string;
  projetNumber?: string;
  projetName?: string;
  customerCode?: string;
  customerName?: string;
}

export interface U3134FindInvoiceItem {
  id: string;
  invoiceNumber?: string | null;
}

export interface U3134FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
}

export const doFindProject = (axios: AxiosInstance, criterias: U3134AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDPROJECT, criterias);
};

export const doFindInvoice = (axios: AxiosInstance, criterias: U3134AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDINVOICE, criterias);
};

export const doFindEmployee = (axios: AxiosInstance, criterias: U3134AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias);
};

/** Mise en page PDF export âge TEC (BL-0596). */
export type U3134TecAgePdfLayout = "tec_age_hierarchy" | "wip_project_list";

/** Corps commun rapport âge TEC et exports async (BL-0027). */
export interface U3134TecAgeRequest {
  asOfDate: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket;
  customerId?: string;
  projectId?: string;
  /** Export PDF uniquement ; absent = hiérarchique (BL-0596). */
  pdfLayout?: U3134TecAgePdfLayout;
}

export interface U3134TecAgeCustomerDetailRequest {
  asOfDate: string;
  customerId: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket;
}

export interface U3134TecAgeBucketSummary {
  bucket: U2000AgingBucket | string;
  lineCount: number;
  totalAmount: number;
}

export interface U3134TecAgeTotals {
  lineCount: number;
  totalAmount: number;
}

export interface U3134TecAgeCustomerRef {
  id: string;
  customerCode?: string;
  customerName?: string;
}

export interface U3134TecAgeProjectRef {
  id: string;
  projetNumber?: string;
  projetName?: string;
}

export interface U3134TecAgeOrganization {
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
}

export interface U3134TecAgeProjectRow {
  project: U3134TecAgeProjectRef;
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
}

export interface U3134TecAgeSummaryCustomerRow {
  customer: U3134TecAgeCustomerRef;
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
}

export interface U3134TecAgeSummaryReport {
  asOfDate: string;
  agingBasis: string;
  organization: U3134TecAgeOrganization;
  customers: U3134TecAgeSummaryCustomerRow[];
}

export interface U3134TecAgeCustomerDetailReport {
  customer: U3134TecAgeCustomerRef;
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
  projects: U3134TecAgeProjectRow[];
}

export interface U3134TecAgeCustomerRow {
  customer: U3134TecAgeCustomerRef;
  buckets: U3134TecAgeBucketSummary[];
  totals: U3134TecAgeTotals;
  projects: U3134TecAgeProjectRow[];
}

export interface U3134TecAgeReport {
  asOfDate: string;
  agingBasis: string;
  organization: U3134TecAgeOrganization;
  customers: U3134TecAgeCustomerRow[];
}

const U3134_TEC_AGE_CURRENCY_OPTS = {
  currencySymbol: "$",
  symbolPosition: "left" as const,
  decimalSeparator: "." as const,
  thousandSeparator: " " as const,
  decimalDigits: 2,
};

export function u3134FormatTecAgeCurrency(amount: number): string {
  return formatCurrency(Number(amount) || 0, U3134_TEC_AGE_CURRENCY_OPTS);
}

export function u3134BuildTecAgeRequestBody(params: {
  asOfDate: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket | null;
  customerId?: string | null;
  projectId?: string | null;
}): U3134TecAgeRequest {
  const body: U3134TecAgeRequest = {
    asOfDate: params.asOfDate.trim().slice(0, 10),
  };
  if (params.lang) body.lang = params.lang;
  if (params.bucket) body.bucket = params.bucket;
  if (params.customerId?.trim()) body.customerId = params.customerId.trim();
  if (params.projectId?.trim()) body.projectId = params.projectId.trim();
  return body;
}

export function u3134BuildTecAgeCustomerDetailRequestBody(params: {
  asOfDate: string;
  customerId: string;
  lang?: "fr" | "en";
  bucket?: U2000AgingBucket | null;
}): U3134TecAgeCustomerDetailRequest {
  const body: U3134TecAgeCustomerDetailRequest = {
    asOfDate: params.asOfDate.trim().slice(0, 10),
    customerId: params.customerId.trim(),
  };
  if (params.lang) body.lang = params.lang;
  if (params.bucket) body.bucket = params.bucket;
  return body;
}

export function u3134TecAgeCacheKey(
  asOfDate: string,
  bucket?: U2000AgingBucket | null,
  customerId?: string,
): string {
  const date = asOfDate.trim().slice(0, 10);
  const b = bucket ?? "";
  const c = customerId ?? "";
  return `${date}|${b}|${c}`;
}

export function u3134TecAgeBucketMap(buckets: U3134TecAgeBucketSummary[]): Map<string, U3134TecAgeBucketSummary> {
  const map = new Map<string, U3134TecAgeBucketSummary>();
  for (const b of buckets ?? []) {
    if (b?.bucket != null) map.set(String(b.bucket), b);
  }
  for (const code of U2000_AGING_BUCKETS) {
    if (!map.has(code)) {
      map.set(code, { bucket: code, lineCount: 0, totalAmount: 0 });
    }
  }
  return map;
}

export function u3134TecAgeEmptyTotals(): U3134TecAgeTotals {
  return { lineCount: 0, totalAmount: 0 };
}

function parseTecAgeBucketSummary(raw: unknown): U3134TecAgeBucketSummary | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const bucket = o.bucket;
  if (bucket == null || bucket === "") return null;
  return {
    bucket: String(bucket),
    lineCount: Number(o.lineCount) || 0,
    totalAmount: Number(o.totalAmount) || 0,
  };
}

function parseTecAgeTotals(raw: unknown): U3134TecAgeTotals {
  if (!raw || typeof raw !== "object") return u3134TecAgeEmptyTotals();
  const o = raw as Record<string, unknown>;
  return {
    lineCount: Number(o.lineCount) || 0,
    totalAmount: Number(o.totalAmount) || 0,
  };
}

function parseTecAgeOrganization(raw: unknown): U3134TecAgeOrganization {
  if (!raw || typeof raw !== "object") {
    return { buckets: [], totals: u3134TecAgeEmptyTotals() };
  }
  const o = raw as Record<string, unknown>;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseTecAgeBucketSummary).filter((b): b is U3134TecAgeBucketSummary => b != null)
    : [];
  return { buckets, totals: parseTecAgeTotals(o.totals) };
}

function parseTecAgeProjectRow(raw: unknown): U3134TecAgeProjectRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const projectRaw = o.project;
  if (!projectRaw || typeof projectRaw !== "object") return null;
  const p = projectRaw as Record<string, unknown>;
  const id = p.id != null ? String(p.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseTecAgeBucketSummary).filter((b): b is U3134TecAgeBucketSummary => b != null)
    : [];
  return {
    project: {
      id,
      projetNumber: p.projetNumber != null ? String(p.projetNumber) : undefined,
      projetName: p.projetName != null ? String(p.projetName) : undefined,
    },
    buckets,
    totals: parseTecAgeTotals(o.totals),
  };
}

function parseTecAgeSummaryCustomerRow(raw: unknown): U3134TecAgeSummaryCustomerRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseTecAgeBucketSummary).filter((b): b is U3134TecAgeBucketSummary => b != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseTecAgeTotals(o.totals),
  };
}

function parseTecAgeCustomerRow(raw: unknown): U3134TecAgeCustomerRow | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseTecAgeBucketSummary).filter((b): b is U3134TecAgeBucketSummary => b != null)
    : [];
  const projects = Array.isArray(o.projects)
    ? o.projects.map(parseTecAgeProjectRow).filter((p): p is U3134TecAgeProjectRow => p != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseTecAgeTotals(o.totals),
    projects,
  };
}

export function parseU3134TecAgeSummaryResponse(data: unknown): U3134TecAgeSummaryReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customers = Array.isArray(o.customers)
    ? o.customers.map(parseTecAgeSummaryCustomerRow).filter((c): c is U3134TecAgeSummaryCustomerRow => c != null)
    : [];
  return {
    asOfDate: o.asOfDate != null ? String(o.asOfDate).slice(0, 10) : "",
    agingBasis: o.agingBasis != null ? String(o.agingBasis) : "TRANSACTION_DATE",
    organization: parseTecAgeOrganization(o.organization),
    customers,
  };
}

export function parseU3134TecAgeCustomerDetailResponse(data: unknown): U3134TecAgeCustomerDetailReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customerRaw = o.customer;
  if (!customerRaw || typeof customerRaw !== "object") return null;
  const c = customerRaw as Record<string, unknown>;
  const id = c.id != null ? String(c.id) : "";
  if (!id) return null;
  const buckets = Array.isArray(o.buckets)
    ? o.buckets.map(parseTecAgeBucketSummary).filter((b): b is U3134TecAgeBucketSummary => b != null)
    : [];
  const projects = Array.isArray(o.projects)
    ? o.projects.map(parseTecAgeProjectRow).filter((p): p is U3134TecAgeProjectRow => p != null)
    : [];
  return {
    customer: {
      id,
      customerCode: c.customerCode != null ? String(c.customerCode) : undefined,
      customerName: c.customerName != null ? String(c.customerName) : undefined,
    },
    buckets,
    totals: parseTecAgeTotals(o.totals),
    projects,
  };
}

export function parseU3134TecAgeReportResponse(data: unknown): U3134TecAgeReport | null {
  const root = data as { result?: unknown };
  const raw = root?.result ?? data;
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const customers = Array.isArray(o.customers)
    ? o.customers.map(parseTecAgeCustomerRow).filter((c): c is U3134TecAgeCustomerRow => c != null)
    : [];
  return {
    asOfDate: o.asOfDate != null ? String(o.asOfDate).slice(0, 10) : "",
    agingBasis: o.agingBasis != null ? String(o.agingBasis) : "TRANSACTION_DATE",
    organization: parseTecAgeOrganization(o.organization),
    customers,
  };
}

export function u3134TecAgeTotalsForBucket(
  buckets: U3134TecAgeBucketSummary[],
  bucket: string,
): U3134TecAgeTotals {
  const summary = u3134TecAgeBucketMap(buckets).get(bucket);
  return {
    lineCount: summary?.lineCount ?? 0,
    totalAmount: summary?.totalAmount ?? 0,
  };
}

export function u3134ReportToSummary(
  report: U3134TecAgeReport,
  bucket?: U2000AgingBucket | null,
): U3134TecAgeSummaryReport {
  if (!bucket) {
    return {
      asOfDate: report.asOfDate,
      agingBasis: report.agingBasis,
      organization: report.organization,
      customers: report.customers.map(({ customer, buckets, totals }) => ({ customer, buckets, totals })),
    };
  }
  const { customers } = u3134FilterTecAgeReportByBucket(report, bucket);
  return {
    asOfDate: report.asOfDate,
    agingBasis: report.agingBasis,
    organization: {
      buckets: report.organization.buckets,
      totals: u3134TecAgeTotalsForBucket(report.organization.buckets, bucket),
    },
    customers: customers.map(({ customer, buckets: b, totals }) => ({ customer, buckets: b, totals })),
  };
}

export function u3134ExtractCustomerDetailFromReport(
  report: U3134TecAgeReport,
  customerId: string,
  bucket?: U2000AgingBucket | null,
): U3134TecAgeCustomerDetailReport | null {
  const row = report.customers.find((c) => c.customer.id === customerId);
  if (!row) return null;
  let projects = row.projects ?? [];
  if (bucket) {
    projects = projects.filter((pr) => {
      const summary = u3134TecAgeBucketMap(pr.buckets).get(bucket);
      return (summary?.lineCount ?? 0) > 0 || Math.abs(summary?.totalAmount ?? 0) > 0;
    });
  }
  return {
    customer: row.customer,
    buckets: row.buckets,
    totals: bucket ? u3134TecAgeTotalsForBucket(row.buckets, bucket) : row.totals,
    projects,
  };
}

export function u3134FormatTecAgeCustomerLabel(customer: U3134TecAgeCustomerRef | undefined): string {
  if (!customer) return "—";
  const code = customer.customerCode?.trim();
  const name = customer.customerName?.trim();
  if (code && name) return `${code} — ${name}`;
  return code || name || customer.id;
}

export function u3134FormatTecAgeProjectLabel(project: U3134TecAgeProjectRef | undefined): string {
  if (!project) return "—";
  const num = project.projetNumber?.trim();
  const name = project.projetName?.trim();
  if (num && name) return `${num} — ${name}`;
  return num || name || project.id;
}

export function u3134FilterTecAgeSummaryByBucket(
  customers: U3134TecAgeSummaryCustomerRow[],
  bucket: U2000AgingBucket,
): U3134TecAgeSummaryCustomerRow[] {
  return customers.filter((row) => {
    const bucketSummary = u3134TecAgeBucketMap(row.buckets).get(bucket);
    return (bucketSummary?.lineCount ?? 0) > 0 || Math.abs(bucketSummary?.totalAmount ?? 0) > 0;
  });
}

export function u3134FilterTecAgeCustomerDetailByBucket(
  detail: U3134TecAgeCustomerDetailReport,
  bucket: U2000AgingBucket,
): U3134TecAgeCustomerDetailReport {
  const projects = detail.projects.filter((pr) => {
    const summary = u3134TecAgeBucketMap(pr.buckets).get(bucket);
    return (summary?.lineCount ?? 0) > 0 || Math.abs(summary?.totalAmount ?? 0) > 0;
  });
  return {
    ...detail,
    totals: u3134TecAgeTotalsForBucket(detail.buckets, bucket),
    projects,
  };
}

export function u3134FilterTecAgeReportByBucket(
  report: U3134TecAgeReport,
  bucket: U2000AgingBucket,
): { customers: U3134TecAgeCustomerRow[] } {
  const customers = report.customers
    .filter((row) => {
      const summary = u3134TecAgeBucketMap(row.buckets).get(bucket);
      return (summary?.lineCount ?? 0) > 0 || Math.abs(summary?.totalAmount ?? 0) > 0;
    })
    .map((row) => ({
      ...row,
      projects: row.projects.filter((pr) => {
        const summary = u3134TecAgeBucketMap(pr.buckets).get(bucket);
        return (summary?.lineCount ?? 0) > 0 || Math.abs(summary?.totalAmount ?? 0) > 0;
      }),
    }));
  return { customers };
}

export const doGetTecAgeReport = (axios: AxiosInstance, body: U3134TecAgeRequest) => {
  return axios.post<{ result: U3134TecAgeReport }>(LogicalUnitEndpoints.TEC_AGE_REPORT, body, u2000SkipBusyIndicatorConfig);
};

export const doGetTecAgeSummary = (axios: AxiosInstance, body: U3134TecAgeRequest) => {
  return axios.post<{ result: U3134TecAgeSummaryReport }>(
    LogicalUnitEndpoints.TEC_AGE_SUMMARY,
    body,
    u2000SkipBusyIndicatorConfig,
  );
};

export const doGetTecAgeCustomerDetail = (axios: AxiosInstance, body: U3134TecAgeCustomerDetailRequest) => {
  return axios.post<{ result: U3134TecAgeCustomerDetailReport }>(
    LogicalUnitEndpoints.TEC_AGE_CUSTOMER_DETAIL,
    body,
    u2000SkipBusyIndicatorConfig,
  );
};

export const doExportTecAgeExcel = (axios: AxiosInstance, body: U3134TecAgeRequest) => {
  return axios.post(LogicalUnitEndpoints.TEC_AGE_EXPORT_EXCEL, body);
};

export const doExportTecAgePdf = (axios: AxiosInstance, body: U3134TecAgeRequest) => {
  return axios.post(LogicalUnitEndpoints.TEC_AGE_EXPORT_PDF, body);
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case "BU313400":
      errorResult.fieldErrors["timesheetHonorariumId"] = t("errBU313400", { ns: nameSpace });
      errorResult.fieldErrors["timesheetExpenseId"] = t("errBU313400", { ns: nameSpace });
      errorResult.fieldErrors["timesheetEquipmentId"] = t("errBU313400", { ns: nameSpace });
      break;
    case "BU313401":
      errorResult.fieldErrors["subproject"] = t("errBU313401", { ns: nameSpace });
      break;
    case "BU313402":
      errorResult.fieldErrors["taskId"] = t("errBU313402", { ns: nameSpace });
      break;
    case "BU313403":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace, defaultValue: "Erreur" }),
        text: t("errBU313403", { ns: nameSpace }),
      });
      break;
    case "BU313404":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace, defaultValue: "Erreur" }),
        text: t("errBU313404", { ns: nameSpace }),
      });
      break;
    case "BU313451":
      errorResult.genericErrors.push({
        title: t("errorTitle", { ns: nameSpace, defaultValue: "Erreur" }),
        text: t("errBU313451", { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
